import { useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { apiRequest } from '../../api/client';

/**
 * The sign-in overlay.
 *
 * Visibility is DERIVED, not commanded, and that is the whole design of this region. The
 * vanilla version had `showLogin()` / `hideLogin()` called from boot, from `enterApp()` and
 * from `logout()` — imperative calls that would race the bundle, because the module script is
 * deferred and `boot()` runs before it. A call arriving before React mounts is simply lost,
 * and the failure would be "the login page never appears", intermittently.
 *
 * So the overlay renders when `booted && !signedIn`, both of which come across the bridge.
 * `booted` is what prevents the other failure: without it, an already-signed-in user would
 * see the login card flash while the session check was still in flight, because the empty
 * snapshot cannot tell "not signed in" from "not asked yet".
 *
 * `showLogin()` and `hideLogin()` survive in the vanilla script as `sync()` calls, so every
 * existing call site keeps working and simply means "the answer may have changed" — the same
 * treatment `renderTags()` got.
 */

interface OidcRefusal {
  reason: string;
  email: string | null;
}

interface OidcConfig {
  oidcEnabled: boolean;
  oidcDisplayName?: string;
  oidcDisplayIconUrl?: string;
  /** Why THIS browser's last Keycloak sign-in was refused, told once (V0.54.4). */
  oidcRefusal?: OidcRefusal | null;
}

const WRONG_CREDENTIALS = 'Wrong username or password.';
const OIDC_FAILED = 'Keycloak sign-in failed. Please try again or use a local account.';

/**
 * The refused person's own reason, in words that say WHO can fix it (V0.54.4). Each reason is
 * mended by a different person — the Keycloak administrator for the first two, InfoStation's
 * for the rest — and a generic "failed" sent everybody to whoever could read the server log.
 * An unknown reason (a newer server) falls back to the generic sentence.
 */
export function oidcRefusalText(r: OidcRefusal | null | undefined): string {
  const address = r?.email ? ` (${r.email})` : '';
  switch (r?.reason) {
    case 'no-email':
      return 'Keycloak did not send an e-mail address for your account, so InfoStation cannot find you. Ask your Keycloak administrator to fill in the e-mail address of your Keycloak profile.';
    case 'unverified':
      return `Your e-mail address${address} is not marked as verified in Keycloak. Ask your Keycloak administrator to verify it, then try again.`;
    case 'unlinked':
      return `No InfoStation account has your e-mail address${address}. Ask an InfoStation administrator to add it to your account, then try again.`;
    case 'linked-elsewhere':
      return `The InfoStation account with your e-mail address${address} is already linked to a different Keycloak account. Ask an InfoStation administrator.`;
    case 'no-default-role':
      return 'InfoStation cannot create an account for you yet. Ask an InfoStation administrator.';
    default:
      return OIDC_FAILED;
  }
}

function EyeIcon({ revealed }: { revealed: boolean }) {
  return (
    <svg className="ico" style={{ width: 17, height: 17 }}>
      <use href={revealed ? '#i-eye-off' : '#i-eye'} />
    </svg>
  );
}

export function LoginOverlay({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const visible = s.booted && !s.currentUser;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [oidc, setOidc] = useState<OidcConfig | null>(null);
  const userRef = useRef<HTMLInputElement>(null);
  /** A failed Keycloak callback's message, held until the card is actually on screen. */
  const pendingError = useRef<string | null>(null);

  useEffect(() => {
    host.classList.toggle('show', visible);
  }, [visible, host]);

  // A failed Keycloak callback comes back here with ?error=oidc. Read it once, and clean the
  // URL — leaving it there means a refresh re-reports a failure that already happened.
  //
  // HELD, NOT SET (V0.54.4). This runs at mount, while the app is still booting and the card is
  // hidden — and the effect below clears the form whenever the card is hidden, in the same
  // pass. Set here, the message was wiped before anyone saw it: a refused Keycloak sign-in came
  // back to a login card that said nothing at all. The effect below applies it once visible.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('error') !== 'oidc') return;
    pendingError.current = OIDC_FAILED;
    history.replaceState(null, '', location.pathname);
  }, []);

  // Which single-sign-on button to offer, if any. Public endpoint, and the display name and
  // icon are env-driven — which is why they are fetched rather than baked into the markup.
  useEffect(() => {
    if (!visible || oidc) return;
    void apiRequest<OidcConfig>('/api/auth/config').then(
      (cfg) => {
        setOidc(cfg);
        // The specific reason replaces the generic sentence `?error=oidc` put up a moment ago.
        if (cfg.oidcRefusal) setError(oidcRefusalText(cfg.oidcRefusal));
      },
      () => setOidc({ oidcEnabled: false }),
    );
  }, [visible, oidc]);

  // Focus the username field when the card appears — including after a logout, which is why
  // this keys on `visible` rather than running once on mount.
  //
  // …and clear the fields when it goes away. The component stays mounted while hidden, so
  // without this the next person to reach the login card finds the last one's username
  // already typed in. The vanilla version cleared both inputs inside logout(); doing it here
  // keeps the form's state where the form is.
  useEffect(() => {
    if (visible) {
      userRef.current?.focus();
      if (pendingError.current) {
        setError(pendingError.current);
        pendingError.current = null;
      }
    } else {
      setUsername('');
      setPassword('');
      setError(null);
      setRevealed(false);
    }
  }, [visible]);

  if (!visible) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const ok = await callAction('attemptLogin', username.trim(), password);
      if (ok) {
        // Clear the password BEFORE the app renders — enterApp() draws the whole map, and
        // this is the difference between the password living in memory for a frame and for
        // as long as this component does. The error message needs no such treatment: the
        // effect above wipes every field the moment the card stops being visible.
        setPassword('');
        await callAction('enterApp');
      } else {
        setError(WRONG_CREDENTIALS);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="login-card" id="login-form" autoComplete="off" onSubmit={submit}>
      <div className="brand">
        <div className="mark">C</div>
        <div>CONIGON Suite</div>
      </div>
      <div className="login-sub">Sign in to open InfoStation</div>

      <label htmlFor="login-user">Username</label>
      <input
        ref={userRef}
        className="fld"
        id="login-user"
        type="text"
        autoComplete="off"
        spellCheck={false}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />

      <label htmlFor="login-pass">Password</label>
      <div className="pwd-wrap">
        <input
          className="fld"
          id="login-pass"
          type={revealed ? 'text' : 'password'}
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          type="button"
          className="pwd-toggle"
          id="login-pass-toggle"
          title="Show / hide password"
          onClick={() => setRevealed((r) => !r)}
        >
          <EyeIcon revealed={revealed} />
        </button>
      </div>

      {/* `.login-error` is display:none in the stylesheet, so the element existing is not the
          same as it being shown. Rendering it only when there is something to say keeps the
          two in step without an inline style fighting the class. */}
      {error ? (
        <div className="login-error" id="login-error" style={{ display: 'block' }}>
          {error}
        </div>
      ) : null}

      <button type="submit" className="pbtn primary" id="login-submit" disabled={busy}>
        <svg className="ico" style={{ width: 16, height: 16, marginRight: 7 }}>
          <use href="#i-lock" />
        </svg>
        Sign in
      </button>

      {oidc?.oidcEnabled ? (
        <button
          type="button"
          className="pbtn"
          id="login-oidc-btn"
          style={{ display: 'flex', marginTop: 10, width: '100%', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => {
            window.location.href = '/api/auth/login/oidc';
          }}
        >
          {oidc.oidcDisplayIconUrl ? (
            <img
              id="login-oidc-icon"
              src={oidc.oidcDisplayIconUrl}
              alt=""
              style={{ width: 16, height: 16, marginRight: 7 }}
            />
          ) : null}
          <span id="login-oidc-name">Login with {oidc.oidcDisplayName || 'SSO'}</span>
        </button>
      ) : null}

      {/* The privacy page, readable BEFORE signing in — that is who it is for. Works here
          because boot() fetches capabilities ahead of the session check, so the page's
          deployment-aware wording is ready while signed out. type="button" so it can never
          submit the form, and tabIndex -1 keeps it out of the form's tab order: tabbing
          through the card stays username -> password -> sign in. */}
      <button
        type="button"
        className="login-privacy"
        id="login-privacy"
        tabIndex={-1}
        onClick={() => callAction('openPrivacy')}
      >
        Privacy
      </button>
    </form>
  );
}
